// Wave 20.49cs — Deterministic map frame for the share card.
//
// The card composites three layers over one Mapbox Static image:
// the route base, a wind-particle still, and large numbered peak
// pins. Both overlays need an EXACT lng/lat ↔ pixel mapping. Static
// Images' `auto` / `[bbox]` modes pick the zoom for you ("most
// detailed zoom level that fits"), which leaves the visible extent
// ambiguous — so we fit the route ourselves and request
// `{lon},{lat},{zoom}` explicitly. From there the projection is
// plain Web Mercator: worldPx = 512·2^zoom, image centered on
// (lon, lat). Nothing to guess.

export type LatLng = { lat: number; lng: number };

export interface MapFrame {
  centerLng: number;
  centerLat: number;
  zoom: number;
  /** CSS-pixel size of the static image (what the projection maps to). */
  width: number;
  height: number;
  /** Visible extent [w, s, e, n] — used as the wind field's bbox. */
  bbox: [number, number, number, number];
  project: (lng: number, lat: number) => { x: number; y: number };
  unproject: (x: number, y: number) => { lng: number; lat: number };
}

const TILE = 512;
const MAX_LAT = 85.0511;

function lngToWorldX(lng: number, worldSize: number): number {
  return ((lng + 180) / 360) * worldSize;
}
function latToWorldY(lat: number, worldSize: number): number {
  const φ = (Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(φ) + 1 / Math.cos(φ)) / Math.PI) / 2) * worldSize;
}
function worldXToLng(x: number, worldSize: number): number {
  return (x / worldSize) * 360 - 180;
}
function worldYToLat(y: number, worldSize: number): number {
  const n = Math.PI - (2 * Math.PI * y) / worldSize;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/** Fit `points` into a `width`×`height` image with `marginFrac` of
 *  breathing room on each axis. Returns null for < 2 points. */
export function fitMapFrame(
  points: Array<LatLng | null>,
  width: number,
  height: number,
  marginFrac = 0.18,
): MapFrame | null {
  const pts = points.filter((p): p is LatLng => p != null && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return null;
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const p of pts) {
    if (p.lng < w) w = p.lng;
    if (p.lng > e) e = p.lng;
    if (p.lat < s) s = p.lat;
    if (p.lat > n) n = p.lat;
  }
  // Extents at zoom 0 (world = 512 px).
  const dx0 = Math.max(1e-6, lngToWorldX(e, TILE) - lngToWorldX(w, TILE));
  const dy0 = Math.max(1e-6, latToWorldY(s, TILE) - latToWorldY(n, TILE));
  const usableW = width * (1 - marginFrac * 2);
  const usableH = height * (1 - marginFrac * 2);
  const zoomX = Math.log2(usableW / dx0);
  const zoomY = Math.log2(usableH / dy0);
  const zoom = Math.max(0, Math.min(20, Math.floor(Math.min(zoomX, zoomY) * 100) / 100));
  const worldSize = TILE * Math.pow(2, zoom);
  const cxWorld = (lngToWorldX(w, worldSize) + lngToWorldX(e, worldSize)) / 2;
  const cyWorld = (latToWorldY(n, worldSize) + latToWorldY(s, worldSize)) / 2;
  const centerLng = worldXToLng(cxWorld, worldSize);
  const centerLat = worldYToLat(cyWorld, worldSize);

  const project = (lng: number, lat: number) => ({
    x: lngToWorldX(lng, worldSize) - cxWorld + width / 2,
    y: latToWorldY(lat, worldSize) - cyWorld + height / 2,
  });
  const unproject = (x: number, y: number) => ({
    lng: worldXToLng(x - width / 2 + cxWorld, worldSize),
    lat: worldYToLat(y - height / 2 + cyWorld, worldSize),
  });
  const tl = unproject(0, 0);
  const br = unproject(width, height);
  return {
    centerLng,
    centerLat,
    zoom,
    width,
    height,
    bbox: [tl.lng, br.lat, br.lng, tl.lat],
    project,
    unproject,
  };
}

/** Mapbox Static Images URL for an explicit frame. `overlays` are
 *  pre-encoded overlay tokens (e.g. route path-…). Max 1280 per side;
 *  request @2x for crisp raster on the card. */
export function staticImageUrlForFrame(
  frame: MapFrame,
  overlays: string[],
  style: string,
  token: string,
  retina = true,
): string {
  const w = Math.min(1280, Math.round(frame.width));
  const h = Math.min(1280, Math.round(frame.height));
  const ov = overlays.length ? `${overlays.join(',')}/` : '';
  return (
    `https://api.mapbox.com/styles/v1/mapbox/${style}/static/` +
    `${ov}${frame.centerLng.toFixed(6)},${frame.centerLat.toFixed(6)},${frame.zoom.toFixed(2)},0,0/` +
    `${w}x${h}${retina ? '@2x' : ''}` +
    `?access_token=${token}&logo=false&attribution=false`
  );
}
